import React from 'react';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Copy, Edit, Play, PauseCircle, Archive, Trash2 } from 'lucide-react';
import { PricingVariant, PricingVariantStatus } from '../../../api/pricingVariants';

interface VariantsListProps {
  isDark: boolean;
  variants: PricingVariant[];
  onEdit: (variant: PricingVariant) => void;
  onClonePlans: (variant: PricingVariant) => void;
  onActivate: (variant: PricingVariant) => void;
  onStopEnrollment: (variant: PricingVariant) => void;
  onArchive: (variant: PricingVariant) => void;
  onDelete: (variant: PricingVariant) => void;
}

const statusLabels: Record<PricingVariantStatus, string> = {
  draft: 'Черновик',
  active: 'Идёт набор',
  enrollment_stopped: 'Набор остановлен',
  archived: 'В архиве',
};

const statusStyles: Record<PricingVariantStatus, string> = {
  draft: 'bg-slate-500/10 text-slate-500',
  active: 'bg-emerald-500/10 text-emerald-500',
  enrollment_stopped: 'bg-amber-500/10 text-amber-500',
  archived: 'bg-red-500/10 text-red-500',
};

const formatMoney = (value: number, currency: string) =>
  new Intl.NumberFormat('uz-UZ', {
    style: 'currency',
    currency: currency || 'UZS',
    minimumFractionDigits: 0,
  }).format(value);

const formatDuration = (value: number, unit: string) => {
  const units: Record<string, string> = { week: 'нед', month: 'мес', year: 'год' };
  return `${value} ${units[unit] ?? unit}`;
};

export function VariantsList({
  isDark,
  variants,
  onEdit,
  onClonePlans,
  onActivate,
  onStopEnrollment,
  onArchive,
  onDelete,
}: VariantsListProps) {
  if (variants.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {variants.map((variant) => {
        const isArchived = variant.status === 'archived';
        const plans = variant.plans ?? [];

        return (
          <Card
            key={variant.id}
            className={cn(
              'rounded-2xl border-0 p-5 transition-all duration-300',
              isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
              isArchived && 'opacity-60',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={cn('font-semibold text-lg', isDark ? 'text-white' : 'text-[#111111]')}>
                    {variant.name}
                  </h3>
                  <Badge className={cn('rounded-lg border-0', statusStyles[variant.status])}>
                    {statusLabels[variant.status]}
                  </Badge>
                  {variant.is_control && (
                    <Badge className="rounded-lg border-0 bg-[#C9A227]/15 text-[#9C7E1B]">
                      контрольный
                    </Badge>
                  )}
                </div>
                <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  Вес набора: {variant.weight}
                  {variant.activated_at &&
                    ` · запущен ${new Date(variant.activated_at).toLocaleDateString('ru-RU')}`}
                </p>
                {variant.description && (
                  <p className={cn('text-sm mt-2', isDark ? 'text-slate-400' : 'text-slate-600')}>
                    {variant.description}
                  </p>
                )}
              </div>
            </div>

            {/* Прайс-лист варианта */}
            <div className="mt-4 space-y-1.5">
              {plans.length === 0 ? (
                <p className={cn('text-sm italic', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  Тарифов нет — вариант нельзя запустить
                </p>
              ) : (
                plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm',
                      isDark ? 'bg-slate-800/50' : 'bg-slate-50',
                      !plan.is_active && 'opacity-50',
                    )}
                  >
                    <div className="min-w-0">
                      <span className={cn('font-mono text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                        {plan.code}
                      </span>
                      <span className={cn('ml-2', isDark ? 'text-slate-300' : 'text-slate-700')}>
                        {formatDuration(plan.duration_value, plan.duration_unit)}
                      </span>
                    </div>
                    <span className={cn('font-medium whitespace-nowrap', isDark ? 'text-white' : 'text-[#111111]')}>
                      {formatMoney(plan.price, plan.currency)}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Действия */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {!isArchived && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(variant)}
                    className={cn('h-8 rounded-lg', isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')}
                  >
                    <Edit className="w-4 h-4 mr-1.5" />
                    Изменить
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onClonePlans(variant)}
                    className={cn('h-8 rounded-lg', isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')}
                  >
                    <Copy className="w-4 h-4 mr-1.5" />
                    Скопировать тарифы
                  </Button>
                </>
              )}

              {variant.status !== 'active' && !isArchived && (
                <Button
                  size="sm"
                  onClick={() => onActivate(variant)}
                  className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-600/90"
                >
                  <Play className="w-4 h-4 mr-1.5" />
                  Запустить набор
                </Button>
              )}

              {variant.status === 'active' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onStopEnrollment(variant)}
                  className={cn('h-8 rounded-lg text-amber-600', isDark ? 'hover:bg-slate-800' : 'hover:bg-amber-50')}
                >
                  <PauseCircle className="w-4 h-4 mr-1.5" />
                  Стоп набора
                </Button>
              )}

              {!isArchived && !variant.is_control && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onArchive(variant)}
                  className={cn('h-8 rounded-lg text-red-600', isDark ? 'hover:bg-slate-800' : 'hover:bg-red-50')}
                >
                  <Archive className="w-4 h-4 mr-1.5" />
                  Архив
                </Button>
              )}

              {/* Удаление разрешает бэкенд: только если варианту никого не назначили
                  и по его тарифам не было заказов. Иначе вернётся понятная ошибка. */}
              {!isArchived && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Удалить вариант"
                  onClick={() => onDelete(variant)}
                  className={cn('h-8 px-2 rounded-lg ml-auto', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400' : 'text-slate-500 hover:bg-slate-100 hover:text-red-600')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
